import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Navigation } from './Navigation';
import { TradeWithMeDisclaimer } from './TradeWithMeDisclaimer';
import { getLeaderTrades, LeaderTrade, TradeSummary } from '../services/tradeWithMeService';
import './Dashboard.css';

const API_BASE = process.env.REACT_APP_API_URL || 'https://4jbhzumjk7.execute-api.us-east-2.amazonaws.com/prod';

const c = {
  bg: '#0f172a', card: '#1f2937', border: '#374151',
  text: '#f9fafb', muted: '#9ca3af', dim: '#6b7280',
  green: '#4ade80', red: '#f87171', purple: '#a78bfa', blue: '#60a5fa',
};

const StatCard: React.FC<{ label: string; value: string | number; color?: string }> = ({ label, value, color = c.text }) => (
  <div style={{ background: c.card, borderRadius: 8, padding: '14px 18px', flex: 1, minWidth: 110 }}>
    <div style={{ color: c.dim, fontSize: 11, marginBottom: 4 }}>{label}</div>
    <div style={{ color, fontSize: 20, fontWeight: 700 }}>{value}</div>
  </div>
);

const fmtDate = (s?: string) => {
  if (!s) return '-';
  try { return new Date(s).toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); }
  catch { return s; }
};

export const TradeWithMe: React.FC = () => {
  const { user, token } = useAuth();
  const [trades, setTrades] = useState<LeaderTrade[]>([]);
  const [summary, setSummary] = useState<TradeSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [showDisclaimer, setShowDisclaimer] = useState(false);

  const enabled = (user as any)?.tradeWithMeEnabled;
  const accepted = (user as any)?.tradeWithMeAccepted;

  const loadTrades = async () => {
    if (!token) return;
    setLoading(true); setError('');
    try {
      const data = await getLeaderTrades(token, { limit: 100 });
      setTrades(data.trades || []);
      setSummary(data.summary || null);
    } catch (err: any) {
      if (err.requiresDisclaimer) {
        setShowDisclaimer(true);
      } else {
        setError(err.message);
      }
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (enabled && accepted) loadTrades();
    else if (enabled && !accepted) setShowDisclaimer(true);
    else setLoading(false);
  }, [token, enabled, accepted, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAcceptDisclaimer = async () => {
    if (!token) return;
    try {
      await fetch(`${API_BASE}/auth/profile`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ tradeWithMeAccepted: true }),
      });
      setShowDisclaimer(false);
      // Reload user context
      window.location.reload();
    } catch { setError('Failed to save disclaimer acceptance'); }
  };

  // Gate: not enabled
  if (!enabled) {
    return (
      <div className="dashboard-container"><Navigation />
        <div style={{ maxWidth: 700, margin: '0 auto', padding: '3rem 1.5rem', textAlign: 'center', color: c.text }}>
          <h2 style={{ marginBottom: '0.75rem' }}>Learn to Trade</h2>
          <p style={{ color: c.muted, maxWidth: 500, margin: '0 auto 1.5rem' }}>
            Study real executed trades for educational purposes. Review entry and exit decisions, P&L outcomes, and performance patterns across options and stock trades.
          </p>
          <a href="/upgrade" style={{ display: 'inline-block', backgroundColor: '#6d28d9', color: 'white', padding: '0.75rem 1.5rem', textDecoration: 'none', borderRadius: 8, fontWeight: 'bold', fontSize: 14 }}>
            Subscribe to Learn to Trade — $200/mo
          </a>
          <p style={{ color: c.dim, fontSize: 13, marginTop: 12 }}>
            Or save with the annual plan: <strong style={{ color: c.purple }}>$1,000/yr</strong> (save $1,400)
          </p>
        </div>
      </div>
    );
  }

  // Disclaimer modal
  if (showDisclaimer) {
    return (
      <div className="dashboard-container"><Navigation />
        <TradeWithMeDisclaimer
          onAccept={handleAcceptDisclaimer}
          onDecline={() => window.history.back()}
        />
      </div>
    );
  }

  const pnlColor = (n: number) => n >= 0 ? c.green : c.red;

  const thStyle: React.CSSProperties = { textAlign: 'left', padding: '8px 10px', color: c.dim, fontSize: 12, borderBottom: `1px solid ${c.border}` };
  const tdStyle: React.CSSProperties = { padding: '8px 10px', fontSize: 13, color: c.text, borderBottom: '1px solid #1f2937' };

  return (
    <div className="dashboard-container"><Navigation />
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '24px 16px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ color: c.text, fontSize: 26, fontWeight: 700, margin: 0 }}>Learn to Trade</h1>
          <p style={{ color: c.muted, fontSize: 14, marginTop: 4 }}>Today's trade activity — study real entry and exit decisions as they happen.</p>
        </div>
        <button onClick={() => setRefreshKey(k => k + 1)} style={{ background: c.card, color: c.muted, border: `1px solid ${c.border}`, borderRadius: 6, padding: '6px 14px', cursor: 'pointer', fontSize: 13 }}>
          Refresh
        </button>
      </div>

      {/* Stats */}
      {summary && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 20 }}>
          <StatCard label="Total Trades" value={summary.totalTrades} />
          <StatCard label="Win Rate" value={`${summary.winRate}%`} color={summary.winRate >= 60 ? c.green : '#fbbf24'} />
          <StatCard label="Total P&L" value={`$${summary.totalPnl.toLocaleString()}`} color={pnlColor(summary.totalPnl)} />
          <StatCard label="Avg P&L" value={`$${summary.avgPnl}`} color={pnlColor(summary.avgPnl)} />
          <StatCard label="W / L" value={`${summary.wins} / ${summary.losses}`} />
        </div>
      )}

      {/* Today's date */}
      <div style={{ marginBottom: 16, color: c.muted, fontSize: 13 }}>
        {new Date().toLocaleDateString('en-US', { timeZone: 'America/New_York', weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })} ET
      </div>

      {/* Error */}
      {error && <div style={{ background: '#dc262620', border: '1px solid #dc2626', borderRadius: 8, padding: '10px 16px', marginBottom: 16, fontSize: 13, color: c.red }}>{error}</div>}

      {/* Loading */}
      {loading && <p style={{ color: c.muted, textAlign: 'center', padding: '3rem' }}>Loading trades...</p>}

      {/* Trades table */}
      {!loading && trades.length > 0 && (
        <div style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: 10, padding: '20px 24px' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thStyle}>Closed</th>
                  <th style={thStyle}>Type</th>
                  <th style={thStyle}>Instrument</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Entry</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Exit</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>P&L</th>
                  <th style={thStyle}>Opened</th>
                </tr>
              </thead>
              <tbody>
                {trades.map((t, i) => {
                  const pnl = Number(t.pnl || 0);
                  const instrument = t.tradeType === 'spread'
                    ? `${t.underlying} ${t.sellStrike}/${t.buyStrike}P ${t.expiration?.slice(5) || ''}`
                    : t.tradeType === 'option'
                    ? `${t.underlying} ${t.sellStrike || t.buyStrike}P ${t.expiration?.slice(5) || ''}`
                    : t.underlying;
                  const typeLabel = t.tradeType === 'spread' ? 'SPREAD' : t.tradeType === 'option' ? 'OPTION' : 'STOCK';
                  const typeColor = t.tradeType === 'stock' ? c.blue : c.purple;
                  return (
                    <tr key={i}>
                      <td style={{ ...tdStyle, fontSize: 11, whiteSpace: 'nowrap' }}>{fmtDate(t.closedAt)}</td>
                      <td style={tdStyle}>
                        <span style={{
                          display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600,
                          background: typeColor + '20', color: typeColor,
                        }}>{typeLabel}</span>
                      </td>
                      <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>{instrument}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>${Number(t.entryPrice).toFixed(2)}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>${Number(t.exitPrice).toFixed(2)}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', color: pnlColor(pnl), fontWeight: 600 }}>
                        {pnl >= 0 ? '+' : ''}${pnl.toFixed(2)}
                      </td>
                      <td style={{ ...tdStyle, fontSize: 11, whiteSpace: 'nowrap', color: c.dim }}>{fmtDate(t.openedAt)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && trades.length === 0 && !error && (
        <div style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: 10, padding: 28, textAlign: 'center' }}>
          <p style={{ color: c.muted }}>No trades opened or closed today yet. Check back during market hours.</p>
        </div>
      )}

      {/* Disclaimer footer */}
      <p style={{ color: c.dim, fontSize: 11, marginTop: 16, textAlign: 'center', lineHeight: 1.6 }}>
        Past performance is not indicative of future results. Wealth Financial Systems LLC is not a financial advisor. All trade examples shown are for educational purposes only and do not constitute investment advice.
        See full <a href="/terms#trade-with-me" style={{ color: c.purple }}>Terms and Conditions</a>.
      </p>
    </div>
    </div>
  );
};

export default TradeWithMe;
